import { createElement, useEffect, useMemo, useState } from 'react';
import { Link } from 'expo-router';

import {
  SidebarDatabaseIcon,
  SidebarPageBlankIcon,
} from '@/components/layout/SidebarIcons.web';
import { buildContextLinkTargets } from '@/lib/chat/context-links';
import type { ContextPin, ContextSet } from '@/lib/chat/types';
import { primeBlockTitle, resolveBlockTitles } from '@/lib/databases/relation-titles';

type ChatContextPinChipsProps = {
  contextSet: ContextSet;
  onContextSetChange: (next: ContextSet) => void;
};

function pinKey(pin: ContextPin): string {
  return pin.blockId ?? pin.fileId ?? pin.kind;
}

function PinIcon({ kind }: { kind: ContextPin['kind'] }) {
  if (kind === 'database') {
    return createElement(SidebarDatabaseIcon);
  }
  return createElement(SidebarPageBlankIcon);
}

export function ChatContextPinChips({ contextSet, onContextSetChange }: ChatContextPinChipsProps) {
  const [titles, setTitles] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    const blockIds = contextSet.pins.map((pin) => pin.blockId).filter(Boolean) as string[];
    if (blockIds.length === 0) {
      setTitles(new Map());
      return;
    }
    void resolveBlockTitles(blockIds).then((resolved) => {
      const next = new Map<string, string>();
      for (const id of blockIds) {
        const title = resolved.get(id)?.title?.trim();
        next.set(id, title && title.length > 0 ? title : 'Untitled');
      }
      setTitles(next);
    });
  }, [contextSet.pins]);

  const linkTargets = useMemo(
    () => buildContextLinkTargets(contextSet, titles),
    [contextSet, titles],
  );
  const hrefByBlockId = useMemo(
    () => new Map(linkTargets.map((target) => [target.id, target.href])),
    [linkTargets],
  );

  if (contextSet.pins.length === 0) {
    return null;
  }

  function removePin(pin: ContextPin) {
    const key = pinKey(pin);
    onContextSetChange({
      ...contextSet,
      pins: contextSet.pins.filter((entry) => pinKey(entry) !== key),
    });
  }

  return createElement(
    'div',
    { className: 'chat-pin-chip-row' },
    contextSet.pins.map((pin) => {
      const label =
        pin.title?.trim() ||
        (pin.blockId != null ? titles.get(pin.blockId) : undefined) ||
        (pin.fileId?.slice(0, 8) ?? pin.kind);
      const href = pin.blockId ? hrefByBlockId.get(pin.blockId) : undefined;

      return createElement(
        'div',
        { key: pinKey(pin), className: 'chat-pin-chip' },
        createElement(
          'span',
          { className: 'chat-pin-chip-icon', 'aria-hidden': true },
          createElement(PinIcon, { kind: pin.kind }),
        ),
        href
          ? createElement(
              Link,
              {
                href,
                className: 'chat-pin-chip-link cs-context-link',
                children: label,
              },
            )
          : createElement('span', { className: 'chat-pin-chip-label' }, label),
        createElement(
          'button',
          {
            type: 'button',
            className: 'chat-pin-chip-remove',
            'aria-label': `Remove ${label} from context`,
            onClick: () => removePin(pin),
          },
          '×',
        ),
      );
    }),
  );
}

/** Call when a pin is added from @-mention so the chip label is immediate. */
export function primeContextPinTitle(id: string, title: string): void {
  primeBlockTitle({ id, title: title.trim() || 'Untitled', deleted: false });
}
